/**
 * RightRail — rail de contexto del chat activo (dirección «Gabinete», #280).
 *
 * «DE QUÉ VA» (resumen), «EN ESTE CHAT» (participantes con rol y color de
 * agente) y «FUENTES CITADAS» (chips, solo si el hilo trae `sources` en algún
 * `answer` — no se inventa el dato si no ha llegado, mismo criterio que la
 * píldora de derivación).
 * Botón "Añadir agente" para incorporar un segundo agente al hilo.
 * Rail colapsable (AG-02b §Rol1 RailDerecho).
 */

import { useState } from 'react'
import { AgentAvatar } from './AgentAvatar.jsx'
import { useSession } from '../context/SessionContext.jsx'
import { agentColorOf } from '../lib/agent-colors.js'
import { avatarUrlOf } from '../lib/images.js'

function AgentPopover({ agent, onClose }) {
  return (
    <div className="agent-popover" role="dialog" aria-label={agent.nombre}>
      <button className="agent-popover__close" onClick={onClose}>✕</button>
      <AgentAvatar name={agent.nombre} avatarUrl={avatarUrlOf(agent)} size={52} />
      <div className="agent-popover__info">
        <strong>{agent.nombre}</strong>
        <span>{agent.rol}</span>
        {agent.departamento && <span className="agent-popover__bloque">{agent.departamento}</span>}
        {agent.descripcion && <p className="agent-popover__desc">{agent.descripcion}</p>}
      </div>
    </div>
  )
}

export default function RightRail({ collapsed, activeChat, sources = [] }) {
  const { agents, agentPalette, resolvedTheme } = useSession()
  const [popoverAgent, setPopoverAgent] = useState(null)

  if (collapsed) return null

  // Si hay chat activo: muestra sus participantes.
  // Si no: muestra los agentes disponibles del descriptor.
  const chatAgents = activeChat
    ? (activeChat.agentes_en_chat ?? []).map(a => {
        const full = agents.find(ag => ag.nombre === a.nombre)
        return full ?? { nombre: a.nombre, rol: a.proposito, bloque: null }
      })
    : []

  const showAvailable = !activeChat
  // Solo los agentes disponibles (operativos), máximo 6 en el rail.
  const availableAgents = agents.filter(a => a.puede_iniciar_chat !== false).slice(0, 6)

  function ringFor(agent) {
    const id = agents.find(a => a.nombre === agent.nombre)?.id
    return id ? agentColorOf(agentPalette ?? {}, id, resolvedTheme) : null
  }

  function renderAgentList(list) {
    return list.map(agent => (
      <div key={agent.nombre} className="right-rail__avatar-wrap">
        <button
          className="right-rail__avatar-btn"
          onClick={() => setPopoverAgent(popoverAgent?.nombre === agent.nombre ? null : agent)}
          title={agent.nombre}
        >
          <AgentAvatar name={agent.nombre} avatarUrl={avatarUrlOf(agent)} size={44} ringColor={ringFor(agent)} />
        </button>
        <span className="right-rail__avatar-info">
          <span className="right-rail__avatar-name">{agent.nombre}</span>
          {agent.rol && <span className="right-rail__avatar-role">{agent.rol}</span>}
        </span>
        {popoverAgent?.nombre === agent.nombre && (
          <AgentPopover
            agent={agent}
            onClose={() => setPopoverAgent(null)}
          />
        )}
      </div>
    ))
  }

  if (showAvailable) {
    return (
      <aside className="right-rail">
        <div className="right-rail__section">
          <p className="right-rail__label">Agentes disponibles</p>
          <div className="right-rail__avatars">
            {renderAgentList(availableAgents)}
          </div>
        </div>
        <p className="right-rail__hint">
          Elige un agente para hablar con él, o escribe y Anne lo enruta.
        </p>
      </aside>
    )
  }

  return (
    <aside className="right-rail">
      {activeChat.de_que_va && (
        <div className="right-rail__section">
          <p className="right-rail__label">De qué va</p>
          <p className="right-rail__de-que-va">{activeChat.de_que_va}</p>
        </div>
      )}

      <div className="right-rail__section">
        <p className="right-rail__label">En este chat</p>
        <div className="right-rail__avatars">
          {renderAgentList(chatAgents)}
        </div>
        <button className="add-agent-btn" title="Añadir un agente a esta conversación">
          + Añadir agente
        </button>
      </div>

      {sources.length > 0 && (
        <div className="right-rail__section">
          <p className="right-rail__label">Fuentes citadas</p>
          <div className="right-rail__sources">
            {sources.map(s => (
              <span key={s.ref ?? s.label} className="source-chip" title={s.ref}>
                {s.label}
              </span>
            ))}
          </div>
        </div>
      )}
    </aside>
  )
}
